<template>
  <div class="ecpf">
    <el-alert type="info" :closable="false" show-icon class="u-mb-10">
      <div slot="title">A senha e a chave privada não saem deste computador</div>
      <div class="obs">
        O sistema pede um <b>desafio</b> ao servidor, assina esse desafio aqui no navegador
        com a chave do seu certificado, e envia ao servidor apenas a <b>assinatura</b> e o
        <b>certificado público</b>. O arquivo <code>.pfx</code> e a senha nunca trafegam.
      </div>
    </el-alert>

    <el-form label-position="top" size="small">

      <el-row :gutter="10">
        <el-col :xs="24" :sm="24" :md="24">
          <el-form-item label="Certificado digital A1 (arquivo .pfx ou .p12)">
            <input ref="pfx" type="file" accept=".pfx,.p12" data-test="input-pfx"
                  @change="arquivoSelecionado" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :xs="24" :sm="24" :md="24">
          <el-form-item label="Senha do certificado">
            <el-input v-model="senha" type="password" show-password style="max-width:320px"
                      data-test="input-senha" @keyup.enter.native="autenticar"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :xs="24" :sm="24" :md="24">
          <el-button type="primary" :loading="autenticando" :disabled="!temArquivo"
                    @click="autenticar" data-test="btn-entrar">
            ENTRAR COM CERTIFICADO
          </el-button>
        </el-col>
      </el-row>
    </el-form>

    <!-- ================= estados ================= -->
    <div v-if="autenticando" class="estado" data-test="estado-autenticando">
      <i class="el-icon-loading"></i> {{ etapa }}
    </div>

    <el-alert v-if="erro" type="error" :closable="false" show-icon class="u-mt-10"
              data-test="estado-erro" :title="erro">
      <div class="obs" v-if="motivo">Código: {{ motivo }}</div>
    </el-alert>

    <el-alert v-if="sucesso" type="success" :closable="false" show-icon class="u-mt-10"
              data-test="estado-sucesso">
      <div slot="title">Autenticado por certificado digital</div>
      <div class="obs">
        CPF do certificado: <b>{{ sucesso.cpf }}</b> · usuário: <b>{{ sucesso.nome }}</b>
      </div>
    </el-alert>
  </div>
</template>

<script>
/**
 * FASE 5 / BLOCO BA-4 — BI 9.
 *
 * Autenticação por e-CPF A1, executando de verdade o desafio/assinatura/validação definidos
 * no backend (`EcpfAutenticacaoService`).
 *
 * Não há atalho aqui: a tela não "lê o CPF do .pfx e manda para o servidor" — isso provaria
 * apenas posse do arquivo público. Ela assina o nonce com a chave privada, e é o servidor
 * que verifica assinatura, validade, cadeia, CPF e vínculo com usuário.
 *
 * A lógica criptográfica mora em `ecpf.js`, módulo puro, testado sem navegador.
 */
import { autenticarComPfx, mensagemDoMotivo } from './ecpf'

export default {
  data () {
    return {
      senha: '',
      temArquivo: false,
      autenticando: false,
      etapa: '',
      erro: null,
      motivo: null,
      sucesso: null
    }
  },

  methods: {
    base () { return this.$store.state.config.baseUrlApi },

    arquivoSelecionado () {
      const input = this.$refs.pfx
      this.temArquivo = !!(input && input.files && input.files.length)
      this.erro = null
      this.sucesso = null
    },

    lerArquivo (file) {
      return new Promise(function (resolve, reject) {
        const leitor = new FileReader()
        leitor.onload = function () {
          // node-forge trabalha com string binária
          const bytes = new Uint8Array(leitor.result)
          let s = ''
          for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
          resolve(s)
        }
        leitor.onerror = function () { reject(new Error('Falha ao ler o arquivo.')) }
        leitor.readAsArrayBuffer(file)
      })
    },

    autenticar () {
      if (this.autenticando) return         // duplo clique não consome dois desafios
      const input = this.$refs.pfx
      if (!input || !input.files || !input.files.length) {
        this.erro = 'Selecione o arquivo do certificado.'
        return
      }

      const self = this
      this.autenticando = true
      this.erro = null
      this.motivo = null
      this.sucesso = null
      this.etapa = 'Abrindo o certificado…'

      this.lerArquivo(input.files[0]).then(function (bufferPfx) {
        // Carregado sob demanda: node-forge é pesado e só faz falta nesta tela.
        return import('node-forge').then(function (mod) {
          const forge = mod.default || mod

          const deps = {
            forge: forge,
            obterDesafio: function () {
              self.etapa = 'Solicitando desafio ao servidor…'
              return self.$axios.get(self.base() + '/api/publico/ecpf/desafio')
                .then(function (r) { return r.data })
            },
            autenticar: function (nonce, assinatura, certificadoPem) {
              self.etapa = 'Enviando assinatura para validação…'
              return self.$axios.post(self.base() + '/api/publico/ecpf/autenticar', {
                nonce: nonce, assinatura: assinatura, certificado: certificadoPem
              }).then(function (r) { return r.data })
                .catch(function (e) {
                  // 401 traz o motivo no corpo — é resposta legítima, não falha de rede.
                  if (e && e.response && e.response.data) return e.response.data
                  throw e
                })
            }
          }

          return autenticarComPfx(deps, bufferPfx, self.senha)
        })
      }).then(function (r) {
        self.autenticando = false
        self.etapa = ''

        if (r && r.autenticado) {
          self.sucesso = r
          self.$emit('autenticado', r)
          return
        }

        self.motivo = r ? r.motivo : null
        self.erro = (r && r.mensagem) ? r.mensagem : mensagemDoMotivo(self.motivo)
      }).catch(function () {
        self.autenticando = false
        self.etapa = ''
        self.erro = 'Falha de comunicação com o servidor.'
      })
    }
  }
}
</script>

<style scoped>
.ecpf {  }
.estado { padding: 14px; color: #909399; font-size: 13px; }
.obs { font-size: 12px; color: #909399; }
</style>
